/**
 * Mineral Resource Statement panel (P0 · Feb 2026).
 *
 * Splits the resource statement into two side-by-side summaries per the
 * P0 directive:
 *
 *   A · Complete Estimated Mineralized Model  (always calculates)
 *   B · Reportable Resource at Selected Cut-off  (may legitimately be zero)
 *
 * Plus a Resource Calculation Reconciliation panel below with pass/fail
 * checks and a per-classification breakdown of the UNCUT model. Every
 * value comes from the /resource/calculate endpoint — no placeholder
 * curves anywhere.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Warning, Certificate, DownloadSimple,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { WizardCard, StatusRow, Pill, LoadingBanner, KV } from "@/pages/wizard/_shared";


export default function ResourceStatementPanel({ projectId, cutoffCurrent, maxGrade, unit }) {
  const defaultCutoffs = useMemo(() => {
    if (!Number.isFinite(maxGrade) || maxGrade <= 0) return [0.0];
    const step = Math.max(+(maxGrade / 10).toFixed(3), 0.001);
    const arr = [];
    for (let i = 0; i <= 10; i++) arr.push(+(step * i).toFixed(3));
    if (Number.isFinite(cutoffCurrent) && !arr.includes(cutoffCurrent))
      arr.push(cutoffCurrent);
    arr.sort((a, b) => a - b);
    return arr;
  }, [maxGrade, cutoffCurrent]);

  const [calc, setCalc] = useState(null);
  const [cutoffsStr, setCutoffsStr] = useState(() => defaultCutoffs.join(", "));

  const runMut = useMutation({
    mutationFn: (cutoffs) => api.post(`/projects/${projectId}/resource/calculate`,
                                      { cutoffs }).then((r) => r.data),
    onSuccess: (d) => { setCalc(d); toast.success("Resource statement calculated"); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message || "Calculation failed"),
  });

  // Auto-run once on mount so the UI populates without an explicit click.
  useEffect(() => {
    if (!projectId) return;
    runMut.mutate(defaultCutoffs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const rerun = () => {
    const parts = cutoffsStr.split(/[\s,]+/).map(Number).filter((n) => Number.isFinite(n) && n >= 0);
    if (!parts.length) return toast.error("Provide at least one numeric cut-off");
    runMut.mutate(parts.sort((a, b) => a - b));
  };

  return (
    <WizardCard title="Mineral Resource Statement"
                 subtitle="Uncut model vs Reportable Resource at selected cut-off"
                 testId="w-resource-panel"
                 right={
                   <Button size="sm" variant="outline" className="h-7 text-[10px]"
                             onClick={rerun} disabled={runMut.isPending}
                             data-testid="w-resource-recalculate">
                     <ArrowClockwise size={11} className="mr-1" />
                     {runMut.isPending ? "Calculating…" : "Recalculate"}
                   </Button>
                 }>
      {runMut.isPending && !calc && <LoadingBanner label="Calculating resource statement…" />}

      {calc && (
        <>
          {/* Warnings (Cut-off above max / Zero above cut-off / No density) */}
          {(calc.warnings || []).map((w, i) => (
            <StatusRow key={i} tone={w.code === "NO_DENSITY" ? "error" : "warn"}>
              <b>{w.code.replace(/_/g, " ")}</b> — {w.message}
            </StatusRow>
          ))}

          {/* A vs B side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <UncutSummary uncut={calc.uncut} unit={calc.diagnostics?.gradeDisplayUnit || unit} commodity={calc.diagnostics?.commodity} />
            <ReportableSummary calc={calc}
                                 selectedCutoff={calc.diagnostics?.selectedCutoff}
                                 unit={calc.diagnostics?.gradeDisplayUnit || unit} />
          </div>

          {/* Per-classification breakdown of the UNCUT model */}
          <div className="rounded border border-slate-200 bg-white p-2"
               data-testid="w-resource-classification-table">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">
              Uncut model · Classification breakdown
            </div>
            <ClassBreakdown results={calc.classificationResults}
                             unit={calc.diagnostics?.gradeDisplayUnit || unit} />
          </div>

          {/* Cut-off sweep table */}
          <div className="rounded border border-slate-200 bg-white p-2 max-h-64 overflow-y-auto"
               data-testid="w-resource-cutoff-table">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">
              Cut-off sensitivity
            </div>
            <div className="table-wrapper">
              <table className="w-full text-[11px] font-mono">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-200">
                    <th className="text-left px-2 py-1">Cut-off ({calc.diagnostics?.gradeDisplayUnit || unit})</th>
                    <th className="text-right px-2 py-1">Blocks</th>
                    <th className="text-right px-2 py-1">Tonnage (t)</th>
                    <th className="text-right px-2 py-1">Grade</th>
                    <th className="text-right px-2 py-1">Contained (oz)</th>
                    <th className="text-right px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {(calc.cutoffResults || []).map((r) => (
                    <tr key={r.cutoff} className="border-t border-slate-100">
                      <td className="px-2 py-0.5">{r.cutoff}</td>
                      <td className="text-right px-2">{r.blocks.toLocaleString()}</td>
                      <td className="text-right px-2">{Math.round(r.tonnage).toLocaleString()}</td>
                      <td className="text-right px-2">{r.averageGrade.toFixed(3)}</td>
                      <td className="text-right px-2">{r.containedMetalOunces.toFixed(1)}</td>
                      <td className="text-right px-2">
                        {r.cutoffAboveMaxGrade && <Pill tone="amber">above max</Pill>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Reconciliation checks */}
          <ReconciliationChecks checks={calc.checks} diagnostics={calc.diagnostics} />

          {/* Cut-off editor for a rerun */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 max-w-lg">
              <Label className="text-[10px] font-semibold text-slate-600">
                Cut-off list (comma-separated)
              </Label>
              <Input value={cutoffsStr}
                       onChange={(e) => setCutoffsStr(e.target.value)}
                       className="h-8 text-[12px] font-mono"
                       data-testid="w-resource-cutoffs-input" />
            </div>
            <Button size="sm" variant="outline"
                       onClick={() => setCutoffsStr(defaultCutoffs.join(", "))}
                       data-testid="w-resource-reset-cutoffs"
                       className="h-8 text-[10px]">
              Reset to 0 → max_grade
            </Button>
          </div>

          <div className="text-[10px] text-slate-500 font-mono">
            Calculation {calc.calculationVersionId?.slice(0, 8)} · {new Date(calc.createdAt).toLocaleString()}
          </div>
        </>
      )}
    </WizardCard>
  );
}


function UncutSummary({ uncut, unit, commodity }) {
  if (!uncut) return null;
  return (
    <div className="rounded border-2 border-brand-blue/40 bg-brand-blue/5 p-3"
          data-testid="w-resource-uncut">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] font-bold text-brand-blue uppercase tracking-widest">
          A · Complete Estimated Mineralized Model
        </div>
        <Pill tone="blue">Uncut</Pill>
      </div>
      <KV k="Blocks"              v={uncut.blocks?.toLocaleString?.()} />
      <KV k="Volume"              v={`${(uncut.volume_m3 / 1e6).toFixed(3)} Mm³`} />
      <KV k="Tonnage"             v={`${(uncut.tonnage / 1e6).toFixed(3)} Mt`} />
      <KV k="Average grade"       v={`${uncut.averageGrade?.toFixed?.(3)} ${unit}`} />
      <KV k="Min grade"           v={`${uncut.minGrade?.toFixed?.(3)} ${unit}`} />
      <KV k="Max grade"           v={`${uncut.maxGrade?.toFixed?.(3)} ${unit}`} />
      <KV k="Contained metal"
          v={uncut.containedMetalOunces > 0
             ? `${uncut.containedMetalOunces?.toFixed?.(1)} oz  ·  ${uncut.containedMetalKg?.toFixed?.(1)} kg`
             : `${uncut.containedMetalUnits?.toFixed?.(2)} t`} />
      <div className="text-[10px] text-brand-blue/80 italic mt-2 leading-tight">
        {uncut.note}
      </div>
    </div>
  );
}


function ReportableSummary({ calc, selectedCutoff, unit }) {
  const above = (calc.cutoffResults || []).find((r) => Math.abs(r.cutoff - selectedCutoff) < 1e-6)
               || (calc.cutoffResults || []).find((r) => r.cutoff >= selectedCutoff)
               || null;
  const zero = !above || above.blocks === 0;
  return (
    <div className={`rounded border-2 p-3 ${zero ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
          data-testid="w-resource-reportable">
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[12px] font-bold uppercase tracking-widest ${zero ? "text-amber-800" : "text-emerald-800"}`}>
          B · Reportable Resource at {selectedCutoff} {unit}
        </div>
        <Pill tone={zero ? "amber" : "green"}>{zero ? "Zero Result (Valid)" : "Reportable"}</Pill>
      </div>
      {!above ? (
        <div className="text-[12px] text-slate-600 italic">
          Selected cut-off not in the sweep. Add {selectedCutoff} to the cut-off list and Recalculate.
        </div>
      ) : (
        <>
          <KV k="Blocks above cut-off"    v={above.blocks?.toLocaleString?.() || 0} />
          <KV k="Tonnage above cut-off"   v={`${(above.tonnage / 1e6).toFixed(3)} Mt`} />
          <KV k="Grade above cut-off"     v={`${above.averageGrade?.toFixed?.(3)} ${unit}`} />
          <KV k="Contained metal above cut-off"
              v={above.containedMetalOunces > 0
                 ? `${above.containedMetalOunces?.toFixed?.(1)} oz  ·  ${above.containedMetalKg?.toFixed?.(1)} kg`
                 : `${above.containedMetalUnits?.toFixed?.(2)} t`} />
          {above.cutoffAboveMaxGrade && (
            <div className="text-[10px] text-amber-900 italic mt-2 leading-tight">
              This cut-off exceeds the maximum estimated grade — a zero result at this cut-off is valid.
              The full mineralized model on the left calculates normally.
            </div>
          )}
        </>
      )}
    </div>
  );
}


function ClassBreakdown({ results, unit }) {
  if (!results) return null;
  const rows = ["Measured", "Indicated", "Measured+Indicated", "Inferred", "Unclassified"];
  return (
    <div className="table-wrapper">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200">
            <th className="text-left px-2 py-1">Class</th>
            <th className="text-right px-2 py-1">Blocks</th>
            <th className="text-right px-2 py-1">Tonnage (Mt)</th>
            <th className="text-right px-2 py-1">Grade ({unit})</th>
            <th className="text-right px-2 py-1">Contained (oz)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((k) => {
            const r = results[k]; if (!r) return null;
            return (
              <tr key={k} className={`border-t border-slate-100 ${k === "Measured+Indicated" ? "bg-emerald-50/50 font-semibold" : ""}`}
                  data-testid={`w-resource-class-${k.replace(/\+/g, "-").toLowerCase()}`}>
                <td className="px-2 py-0.5">{k}</td>
                <td className="text-right px-2">{r.blocks.toLocaleString()}</td>
                <td className="text-right px-2">{(r.tonnage / 1e6).toFixed(3)}</td>
                <td className="text-right px-2">{r.averageGrade.toFixed(3)}</td>
                <td className="text-right px-2">{r.containedMetalOunces?.toFixed?.(1) ?? "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function ReconciliationChecks({ checks, diagnostics }) {
  if (!checks?.length) return null;
  const allPass = checks.every((c) => c.pass);
  return (
    <div className={`rounded border p-3 ${allPass ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
          data-testid="w-resource-reconciliation">
      <div className={`text-[12px] font-bold uppercase tracking-widest mb-2 ${allPass ? "text-emerald-800" : "text-amber-800"}`}>
        Resource Calculation Reconciliation
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[11px] font-mono">
        {checks.map((c) => (
          <div key={c.name} className="flex items-center gap-2"
                data-testid={`w-resource-check-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {c.pass
              ? <CheckCircle size={13} weight="fill" className="text-emerald-600 shrink-0" />
              : <XCircle     size={13} weight="fill" className="text-amber-700  shrink-0" />}
            <span className={c.pass ? "text-slate-700" : "text-amber-900 font-semibold"}>{c.name}</span>
          </div>
        ))}
      </div>
      {diagnostics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[10px] font-mono">
          <MiniStat  label="Total blocks"       value={diagnostics.totalBlocks} />
          <MiniStat  label="Est. blocks"        value={diagnostics.estimatedBlocks} />
          <MiniStat  label="Density valid"      value={diagnostics.blocksWithDensity} />
          <MiniStat  label="Classified"         value={diagnostics.classifiedBlocks} />
          <MiniStat  label="Above cut-off"      value={diagnostics.blocksAboveSelectedCutoff} />
          <MiniStat  label="Selected cut-off"   value={`${diagnostics.selectedCutoff} ${diagnostics.gradeDisplayUnit || ""}`} />
          <MiniStat  label="Max grade"          value={`${diagnostics.maxEstimatedGrade?.toFixed?.(3)} ${diagnostics.gradeDisplayUnit || ""}`} />
          <MiniStat  label="Grade field"        value={diagnostics.gradeField} />
        </div>
      )}
    </div>
  );
}


function MiniStat({ label, value }) {
  return (
    <div className="bg-white/70 rounded border border-slate-200 p-1.5">
      <div className="text-slate-500 uppercase text-[8px] tracking-widest">{label}</div>
      <div className="text-slate-900 font-bold text-[12px] mt-0.5 truncate">{value}</div>
    </div>
  );
}
